import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';

interface LogoUploaderProps {
  /** 已保存在服务器上的 Logo 地址（公开可访问，不需要鉴权下载），没有 Logo 时为空 */
  existingUrl?: string | null;
  /** 本次弹窗里新选的、还没提交的文件 */
  stagedFile: File | null;
  onStagedFileChange: (file: File | null) => void;
  /** 标记为"保存时删除已有 Logo"；选了新文件会自动清掉这个标记，以新文件为准 */
  markedForRemoval: boolean;
  onMarkedForRemovalChange: (marked: boolean) => void;
}

/**
 * 承运商 Logo 上传控件：缩略图 + "查看"/"删除"按钮，改动只是"暂存"，真正提交/回滚
 * 由外层的保存按钮统一处理（这个弹窗本来就有"保存"按钮，不需要像地址库那样立即生效）。
 */
const LogoUploader: React.FC<LogoUploaderProps> = ({
  existingUrl, stagedFile, onStagedFileChange, markedForRemoval, onMarkedForRemovalChange,
}) => {
  const [stagedPreviewUrl, setStagedPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!stagedFile) {
      setStagedPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(stagedFile);
    setStagedPreviewUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [stagedFile]);

  const previewSrc = stagedPreviewUrl || (!markedForRemoval ? existingUrl : null);

  const uploadProps: UploadProps = {
    multiple: false,
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file) => {
      onStagedFileChange(file as File);
      onMarkedForRemovalChange(false);
      return false;
    },
  };

  const handleRemove = () => {
    if (stagedFile) {
      // 只是取消这次还没提交的新选择，还原成底下已保存的 Logo（如果有的话）
      onStagedFileChange(null);
      return;
    }
    onMarkedForRemovalChange(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Upload {...uploadProps}>
          {previewSrc ? (
            <div
              style={{
                width: 64, height: 64, border: '1px solid #d9d9d9', borderRadius: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#fafafa', cursor: 'pointer', overflow: 'hidden',
              }}
            >
              <img src={previewSrc} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          ) : (
            <Button icon={<UploadOutlined />}>选择图片</Button>
          )}
        </Upload>
        {previewSrc && (
          <Space direction="vertical" size={4}>
            <Button size="small" onClick={() => setPreviewOpen(true)}>查看</Button>
            <Button size="small" danger onClick={handleRemove}>删除</Button>
          </Space>
        )}
        {!previewSrc && markedForRemoval && (
          <Button size="small" onClick={() => onMarkedForRemovalChange(false)}>撤销删除</Button>
        )}
      </div>
      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        title="Logo 预览"
        centered
        footer={<Button onClick={() => setPreviewOpen(false)}>关闭</Button>}
      >
        {previewSrc && <img src={previewSrc} alt="Logo" style={{ width: '100%' }} />}
      </Modal>
    </div>
  );
};

export default LogoUploader;
